import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import { baseApiUrl, apiHeaders } from "@openimis/fe-core";

const trimTrailingSlash = (value) => value.replace(/\/$/, "");
const TRANSLATION_DRAFT_KEY_PREFIX = "openimis_translation_draft_";

const fileNameForLanguage = (language) =>
  language === "my" ? "burmese.json" : "en.json";

const resolveApiBases = () => {
  const configured = (window.localStorage.getItem("openimis_backend_base_url") || "").trim();
  const sameOrigin = `${window.location.protocol}//${window.location.host}`;
  const normalizedConfigured = configured ? trimTrailingSlash(configured) : "";
  const normalizedCoreApiBase = trimTrailingSlash(baseApiUrl || "");

  const candidates = [];
  const push = (value) => {
    const v = (value || "").trim();
    if (!v) return;
    candidates.push(trimTrailingSlash(v));
  };

  if (normalizedConfigured) {
    push(normalizedConfigured);
    if (normalizedConfigured.endsWith("/api")) {
      push(normalizedConfigured.slice(0, -4));
    }
  }

  if (
    normalizedCoreApiBase.startsWith("http://") ||
    normalizedCoreApiBase.startsWith("https://")
  ) {
    push(normalizedCoreApiBase);
    if (normalizedCoreApiBase.endsWith("/api")) {
      push(normalizedCoreApiBase.slice(0, -4));
    }
  } else if (normalizedCoreApiBase.startsWith("/")) {
    const absoluteApiBase = `${sameOrigin}${normalizedCoreApiBase}`;
    push(absoluteApiBase);
    if (absoluteApiBase.endsWith("/api")) {
      push(absoluteApiBase.slice(0, -4));
    }
  }

  push(sameOrigin);

  return Array.from(new Set(candidates));
};

const buildCandidates = (baseCandidates, fileName) => {
  const urls = [];
  const pushPair = (value) => {
    urls.push(value);
    urls.push(`${value}/`);
  };

  baseCandidates.forEach((base) => {
    const normalizedBase = trimTrailingSlash(base);
    pushPair(`${normalizedBase}/api/mobile/i18n/${fileName}`);
    pushPair(`${normalizedBase}/api/api/mobile/i18n/${fileName}`);
    pushPair(`${normalizedBase}/i18n/${fileName}`);
    pushPair(`${normalizedBase}/mobile/i18n/${fileName}`);
    if (normalizedBase.endsWith("/api")) {
      pushPair(`${normalizedBase}/mobile/i18n/${fileName}`);
      pushPair(`${normalizedBase}/api/mobile/i18n/${fileName}`);
    }
  });

  return Array.from(new Set(urls));
};

const parseJsonObject = (value) => {
  const parsed = JSON.parse(value);
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error("JSON must be an object: { \"key\": \"value\" }");
  }
  return parsed;
};

const computeDiffSummary = (beforeObj, afterObj) => {
  if (!beforeObj || !afterObj) return { added: 0, updated: 0, removed: 0 };
  const beforeKeys = Object.keys(beforeObj);
  const afterKeys = Object.keys(afterObj);
  const beforeSet = new Set(beforeKeys);
  const afterSet = new Set(afterKeys);

  let added = 0;
  let removed = 0;
  let updated = 0;

  afterKeys.forEach((key) => {
    if (!beforeSet.has(key)) {
      added += 1;
      return;
    }
    if ((beforeObj[key] ?? "") !== (afterObj[key] ?? "")) {
      updated += 1;
    }
  });

  beforeKeys.forEach((key) => {
    if (!afterSet.has(key)) {
      removed += 1;
    }
  });

  return { added, updated, removed };
};

const TranslationAdminPage = () => {
  const [language, setLanguage] = React.useState("en");
  const [jsonText, setJsonText] = React.useState("{}");
  const [busy, setBusy] = React.useState(false);
  const [status, setStatus] = React.useState("Load translation JSON from server.");
  const [attempts, setAttempts] = React.useState([]);
  const [lastSyncedAt, setLastSyncedAt] = React.useState("");
  const [lastServerSnapshot, setLastServerSnapshot] = React.useState(null);

  const draftStorageKey = React.useMemo(
    () => `${TRANSLATION_DRAFT_KEY_PREFIX}${language}`,
    [language],
  );

  React.useEffect(() => {
    const draft = window.localStorage.getItem(draftStorageKey);
    if (!draft) return;
    try {
      const parsed = JSON.parse(draft);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        setJsonText(JSON.stringify(parsed, null, 2));
        setStatus(`Loaded local draft for ${fileNameForLanguage(language)} from this browser.`);
      }
    } catch (_) {
      // Ignore invalid draft and keep current text.
    }
  }, [draftStorageKey, language]);

  const loadServer = React.useCallback(async () => {
    const baseCandidates = resolveApiBases();
    const fileName = fileNameForLanguage(language);
    setBusy(true);
    setStatus("Loading translation from server...");
    setAttempts([]);

    try {
      const candidates = buildCandidates(baseCandidates, fileName);
      const attemptLog = [];
      for (const url of candidates) {
        try {
          const response = await fetch(`${url}?t=${Date.now()}`, {
            method: "GET",
            credentials: "include",
            headers: {
              ...apiHeaders(),
              Accept: "application/json",
            },
            cache: "no-store",
          });
          if (!response.ok) {
            attemptLog.push(`GET ${url} -> ${response.status}`);
            continue;
          }
          const data = await response.json();
          if (data && typeof data === "object" && !Array.isArray(data)) {
            setJsonText(JSON.stringify(data, null, 2));
            setLastServerSnapshot(data);
            setLastSyncedAt(new Date().toLocaleString());
            setStatus(`Loaded ${Object.keys(data).length} keys from ${url}`);
            setAttempts(attemptLog);
            setBusy(false);
            return;
          }
          attemptLog.push(`GET ${url} -> invalid JSON object`);
        } catch (_) {
          attemptLog.push(`GET ${url} -> network/auth/cors error`);
        }
      }
      setStatus("Could not load translation JSON from server endpoints.");
      setAttempts(attemptLog);
    } finally {
      setBusy(false);
    }
  }, [language]);

  const saveToServer = React.useCallback(async () => {
    let parsed;
    try {
      parsed = parseJsonObject(jsonText);
    } catch (error) {
      setStatus(`Invalid JSON: ${error?.message || error}`);
      return;
    }

    const baseCandidates = resolveApiBases();
    const fileName = fileNameForLanguage(language);
    const candidates = buildCandidates(baseCandidates, fileName);

    setBusy(true);
    setStatus("Saving translation to server...");
    setAttempts([]);

    try {
      const attemptLog = [];
      const beforeSnapshot = lastServerSnapshot;
      for (const url of candidates) {
        try {
          const putResp = await fetch(url, {
            method: "PUT",
            credentials: "include",
            headers: {
              ...apiHeaders(),
              "Content-Type": "application/json",
            },
            body: JSON.stringify(parsed),
          });
          if (putResp.ok) {
            let finalData = parsed;
            try {
              const reloadResp = await fetch(`${url}?t=${Date.now()}`, {
                method: "GET",
                credentials: "include",
                headers: {
                  ...apiHeaders(),
                  Accept: "application/json",
                },
                cache: "no-store",
              });
              if (reloadResp.ok) {
                const reloaded = await reloadResp.json();
                if (reloaded && typeof reloaded === "object" && !Array.isArray(reloaded)) {
                  finalData = reloaded;
                }
              }
            } catch (_) {
              // Keep parsed as finalData if reload fails.
            }

            const diff = computeDiffSummary(beforeSnapshot, finalData);
            const now = new Date().toLocaleString();

            setJsonText(JSON.stringify(finalData, null, 2));
            setLastServerSnapshot(finalData);
            setLastSyncedAt(now);
            setStatus(
              `Saved ${Object.keys(finalData).length} keys to ${url}. Added ${diff.added}, updated ${diff.updated}, removed ${diff.removed}.`,
            );
            setAttempts(attemptLog);
            setBusy(false);
            return;
          }
          attemptLog.push(`PUT ${url} -> ${putResp.status}`);
        } catch (_) {
          attemptLog.push(`PUT ${url} -> network/auth/cors error`);
        }

        try {
          const postResp = await fetch(url, {
            method: "POST",
            credentials: "include",
            headers: {
              ...apiHeaders(),
              "Content-Type": "application/json",
            },
            body: JSON.stringify(parsed),
          });
          if (postResp.ok) {
            let finalData = parsed;
            try {
              const reloadResp = await fetch(`${url}?t=${Date.now()}`, {
                method: "GET",
                credentials: "include",
                headers: {
                  ...apiHeaders(),
                  Accept: "application/json",
                },
                cache: "no-store",
              });
              if (reloadResp.ok) {
                const reloaded = await reloadResp.json();
                if (reloaded && typeof reloaded === "object" && !Array.isArray(reloaded)) {
                  finalData = reloaded;
                }
              }
            } catch (_) {
              // Keep parsed as finalData if reload fails.
            }

            const diff = computeDiffSummary(beforeSnapshot, finalData);
            const now = new Date().toLocaleString();

            setJsonText(JSON.stringify(finalData, null, 2));
            setLastServerSnapshot(finalData);
            setLastSyncedAt(now);
            setStatus(
              `Saved ${Object.keys(finalData).length} keys to ${url}. Added ${diff.added}, updated ${diff.updated}, removed ${diff.removed}.`,
            );
            setAttempts(attemptLog);
            setBusy(false);
            return;
          }
          attemptLog.push(`POST ${url} -> ${postResp.status}`);
        } catch (_) {
          attemptLog.push(`POST ${url} -> network/auth/cors error`);
        }
      }

      setStatus(
        "Save failed. Server endpoint is not writable or auth is missing. Use Save Draft/Download JSON as fallback.",
      );
      setAttempts(attemptLog);
    } finally {
      setBusy(false);
    }
  }, [jsonText, language, lastServerSnapshot]);

  const saveDraft = React.useCallback(() => {
    let parsed;
    try {
      parsed = parseJsonObject(jsonText);
    } catch (error) {
      setStatus(`Invalid JSON: ${error?.message || error}`);
      return;
    }

    window.localStorage.setItem(draftStorageKey, JSON.stringify(parsed));
    setStatus(`Saved draft locally in browser for ${fileNameForLanguage(language)}.`);
  }, [draftStorageKey, jsonText, language]);

  const downloadJson = React.useCallback(() => {
    let parsed;
    try {
      parsed = parseJsonObject(jsonText);
    } catch (error) {
      setStatus(`Invalid JSON: ${error?.message || error}`);
      return;
    }

    const fileName = fileNameForLanguage(language);
    const blob = new Blob([JSON.stringify(parsed, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setStatus(`Downloaded ${fileName}. Upload it to server/static storage if API save is unavailable.`);
  }, [jsonText, language]);

  React.useEffect(() => {
    loadServer();
  }, [language, loadServer]);

  return (
    <Box p={2}>
      <Typography variant="h6" style={{ marginBottom: 8 }}>
        Translation Admin
      </Typography>
      <Typography style={{ color: "#555", marginBottom: 16 }}>
        Load translation JSON from server, edit keys, and save once for all remote sites.
      </Typography>

      <Paper style={{ padding: 16, borderRadius: 12 }}>
        <TextField
          select
          fullWidth
          label="Language file"
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          variant="outlined"
          style={{ marginBottom: 10 }}
        >
          <MenuItem value="en">English (en.json)</MenuItem>
          <MenuItem value="my">Burmese (burmese.json)</MenuItem>
        </TextField>

        <Box display="flex" gridGap={8} style={{ marginBottom: 12 }}>
          <Button variant="outlined" color="primary" onClick={loadServer} disabled={busy}>
            Load Server
          </Button>
          <Button variant="contained" color="primary" onClick={saveToServer} disabled={busy}>
            Save To Server
          </Button>
          <Button variant="outlined" onClick={saveDraft} disabled={busy}>
            Save Draft
          </Button>
          <Button variant="outlined" onClick={downloadJson} disabled={busy}>
            Download JSON
          </Button>
        </Box>

        <TextField
          value={jsonText}
          onChange={(e) => setJsonText(e.target.value)}
          multiline
          rows={16}
          variant="outlined"
          label="Translation JSON"
          fullWidth
          style={{ marginBottom: 12 }}
        />

        <Typography style={{ marginBottom: 10, color: status.toLowerCase().includes("failed") ? "#d32f2f" : "#333" }}>
          {status}
        </Typography>

        {lastSyncedAt && (
          <Typography style={{ marginBottom: 10, color: "#666", fontSize: 12 }}>
            Last synced at: {lastSyncedAt}
          </Typography>
        )}

        {attempts.length > 0 && (
          <Paper style={{ padding: 10, marginBottom: 12, background: "#fafafa" }}>
            <Typography variant="subtitle2" style={{ marginBottom: 6 }}>
              Endpoint attempts
            </Typography>
            {attempts.map((line, idx) => (
              <Typography key={`${line}-${idx}`} style={{ fontSize: 12, color: "#444" }}>
                {line}
              </Typography>
            ))}
          </Paper>
        )}

        {busy && <CircularProgress size={22} />}
        <Divider style={{ marginTop: 12 }} />
      </Paper>
    </Box>
  );
}

export default TranslationAdminPage;
